<template>
  <div>
    <LoadingWrapper
      v-if="isCompetencyScoringMethodByAssessor"
      :loading="getLoading"
    >
      <template #loading>
        <b-skeleton
          v-for="(item, index) in [1, 2, 3]"
          :key="`indicator-loading-${index}`"
          class="width-percent-100 height-80 margin-bottom-16"
        ></b-skeleton>
      </template>
      <template #default>
        <b-row class="margin-0">
          <b-col cols="12" class="padding-0 margin-bottom-16 flex-end">
            <div
              class="background-surface color-complementary-400 border--solid border--complementary-50 border-radius-8 font-size-14 font-weight-500 flex padding-y-8 padding-x-12 cursor--pointer disable-select width--max-content mobile-width-percent-100 mobile-flex-justify-content-center tablet-width-percent-100 tablet-flex-justify-content-center"
            >
              <span
                class="margin-left-8"
                @click.stop="handleChangeCollapseGroupVisible"
              >
                <mdicon
                  :name="
                    collapseGroupVisible
                      ? 'unfold-less-horizontal'
                      : 'unfold-more-horizontal'
                  "
                  size="20"
                />
                {{
                  collapseGroupVisible
                    ? $lang.closeAllCards
                    : $lang.openAllCards
                }}
              </span>
            </div>
          </b-col>
          <b-col
            v-for="({ caption, score, description }, index) in competencyItems"
            :key="`competency-${index}`"
            cols="12"
            class="padding-0"
          >
            <CollapseCard
              :id="`collapse-${index}`"
              accordion=""
              :visible="collapseGroupVisible"
            >
              <template #header>
                <div class="flex-between width-percent-100">
                  <span class="font-size-16 color-complementary-400">
                    {{ caption }}
                  </span>
                  <span
                    v-if="score"
                    class="font-size-16 color-complementary-300 margin-left-8"
                  >
                    {{ score }} {{ $lang.score }}
                  </span>
                </div>
              </template>
              <template #body>
                <div class="color-complementary-400" v-html="description"></div>
              </template>
            </CollapseCard>
          </b-col>
        </b-row>
      </template>
    </LoadingWrapper>
    <b-row class="margin-0">
      <b-col
        v-if="isCompetencyScoringMethodByAssessor"
        cols="12"
        class="padding-0"
      >
        <OverviewForm
          :exerciseData="exerciseData"
          @handleUpdate="updateOverviewFormData"
        />
      </b-col>
      <b-col cols="12" class="padding-0">
        <b-row
          class="margin-0 margin-bottom-16 width-percent-100 background-surface border--solid border--complementary-50 border-radius-8 padding-16"
        >
          <b-col
            cols="12"
            class="padding-0 margin-bottom-16 font-size-16 color-competency-400"
          >
            {{ $lang.changeParvandehStatus }}
          </b-col>
          <b-col cols="12" class="padding-0">
            <ChangeStatus @handleSubmit="handleSubmitChangeStatus" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { LoadingWrapper, CollapseCard } from "@/components/re-design";
import OverviewForm from "./components/overview/OverviewForm.vue";
import ChangeStatus from "./components/ChangeStatus.vue";
import { pathOr } from "ramda";
import { generateError, goToRoute, toastAlert } from "@/shared/utils";
import {
  getCenterExecuteCaseOverview,
  setComputeCompetencyOverview,
} from "@/modules/kanoon/services";
import { inject, onMounted, ref } from "vue";
export default {
  components: { LoadingWrapper, CollapseCard, OverviewForm, ChangeStatus },
  setup() {
    // bind injects data
    const centerExecuteId = inject("CENTER_EXECUTE_ID");
    const caseId = inject("CASE_ID");
    // bind injects data
    const isCompetencyScoringMethodByAssessor = inject(
      "IS_COMPETENCY_SCORING_METHOD_BY_ASSESSOR"
    );

    // fetch data competencies and exercise
    const getLoading = ref(false);
    const competencyItems = ref([]);
    const exerciseData = ref(null);
    const fetchData = async () => {
      if (!isCompetencyScoringMethodByAssessor.value) return;

      try {
        getLoading.value = true;
        const response = await getCenterExecuteCaseOverview(
          centerExecuteId.value,
          caseId.value
        );
        const { competencies, exercise } = response;
        competencyItems.value = competencies;
        exerciseData.value = exercise;
      } catch (error) {
        generateError(error);
      } finally {
        getLoading.value = false;
      }
    };
    onMounted(() => fetchData());

    const goToCenterExecuteCases = () => {
      const name = "center-execute-cases";
      const params = { centerExecuteId: centerExecuteId.value };
      goToRoute(name, params, {}, false);
    };

    // handle submit
    const overviewFormData = {
      candidateOverallScore: null,
      strengthAndDevelopment: null,
      exerciseSummary: null,
    };
    const updateOverviewFormData = (key, value) =>
      (overviewFormData[key] = value);

    const handleSubmitChangeStatus = async (caseStatusId, callback) => {
      const candidateOverallScoreId = pathOr(
        null,
        ["candidateOverallScore", "id"],
        overviewFormData
      );
      const exerciseSummary = pathOr(
        null,
        ["exerciseSummary"],
        overviewFormData
      );
      const strengthAndDevelopment = pathOr(
        null,
        ["strengthAndDevelopment"],
        overviewFormData
      );
      try {
        const response = await setComputeCompetencyOverview({
          centerExecuteId: centerExecuteId.value,
          caseId: caseId.value,
          caseStatusId,
          candidateOverallScoreId,
          strengthAndDevelopment,
          exerciseSummary,
        });
        const { message } = response;
        toastAlert(message);
        goToCenterExecuteCases();
      } catch (error) {
        generateError(error);
      } finally {
        callback();
      }
    };

    // handle visible group collapses
    const collapseGroupVisible = ref(false);
    const handleChangeCollapseGroupVisible = () => {
      collapseGroupVisible.value = !collapseGroupVisible.value;
    };

    // return data to be used in template
    return {
      getLoading,
      competencyItems,
      exerciseData,
      handleSubmitChangeStatus,
      updateOverviewFormData,
      isCompetencyScoringMethodByAssessor,
      collapseGroupVisible,
      handleChangeCollapseGroupVisible,
    };
  },
};
</script>
